import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  city: "",
  message: "",
};

export const Wholesale = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/wholesale`, form);
      toast.success("Enquiry received. Our team will reach out within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Couldn't submit enquiry. Please check the fields and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="wholesale"
      data-testid="wholesale-section"
      className="relative py-24 lg:py-32 bg-[#EAE3D9]/70 kraft-grain"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
            <Building2 size={14} /> Wholesale · B2B
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-mo-ink leading-[1.05]">
            Stock Maati in <span className="italic text-mo-green">your store.</span>
          </h2>
          <p className="mt-6 text-mo-ink-soft max-w-md">
            We partner with restaurants, cafes, cloud kitchens, organic stores,
            and curated gift brands across India. Fill the form — our team
            replies within a working day.
          </p>

          <ul className="mt-8 space-y-3 text-sm text-mo-ink-soft">
            <li className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-mo-terracotta" />
              Custom pack sizes (100g to 5kg, bulk tins on request)
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-mo-terracotta" />
              White-label & private label available
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-mo-terracotta" />
              Pan-India logistics · Export enquiries welcome
            </li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="wholesale-form"
          className="lg:col-span-7 bg-mo-cream rounded-3xl p-8 sm:p-10 border border-[#5C5248]/12 shadow-xl"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Input
              label="Your name"
              value={form.name}
              onChange={onChange("name")}
              required
              testId="wholesale-input-name"
            />
            <Input
              label="Business name"
              value={form.business_name}
              onChange={onChange("business_name")}
              testId="wholesale-input-business"
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              required
              testId="wholesale-input-email"
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={onChange("phone")}
              required
              testId="wholesale-input-phone"
            />
            <Input
              label="City"
              value={form.city}
              onChange={onChange("city")}
              testId="wholesale-input-city"
            />
          </div>

          <div className="mt-5">
            <label className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
              Tell us a little about your business
            </label>
            <textarea
              rows={4}
              value={form.message}
              onChange={onChange("message")}
              data-testid="wholesale-input-message"
              className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40 focus:border-[#B33922]/60 resize-none"
              placeholder="Expected volume, location, use case…"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="wholesale-submit"
            className="mt-7 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-mo-terracotta text-mo-cream font-medium hover:bg-[#8F2B17] transition-all duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : null}
            Send enquiry
          </button>
        </form>
      </div>
    </section>
  );
};

const Input = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
      {label}
    </span>
    <input
      {...props}
      data-testid={testId}
      className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40 focus:border-[#B33922]/60"
    />
  </label>
);
